import type React from "react";
import { useCallback } from "react";

import type { PortfolioChatDto } from "@/api/types";
import { Box } from "@/design-system/Box";
import { Card } from "@/design-system/Card";
import { Inline } from "@/design-system/Inline";
import { SectionCard } from "@/design-system/SectionCard";
import { Spinner } from "@/design-system/Spinner";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { useTranslation } from "@/translations";

import { PortfolioChatListItem } from "./PortfolioChatListItem";

interface PortfolioChatListViewProps {
  chats: PortfolioChatDto[];
  fetchMoreChats: (() => void) | undefined;
  isFetchingMoreChats: boolean;
  hasMoreChats: boolean;
  onOpenChat: (chat: PortfolioChatDto) => void;
}

export function PortfolioChatListView({
  chats,
  isFetchingMoreChats,
  hasMoreChats,
  fetchMoreChats,
  onOpenChat,
}: PortfolioChatListViewProps): React.ReactNode {
  const { t } = useTranslation();
  const refLoader = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isFetchingMoreChats && hasMoreChats) {
        fetchMoreChats?.();
      }
    }, [fetchMoreChats, hasMoreChats, isFetchingMoreChats]),
  });

  if (chats.length === 0) {
    return (
      <SectionCard>
        <span className="text-caption text-text-secondary">{t("page.portfolio.chats.no-chats")}</span>
      </SectionCard>
    );
  }

  return (
    <Card as="section" density="flush" scroll="content">
      <ul data-testid="portfolio-chat-list" className="overflow-hidden">
        {chats.map((chat) => (
          <PortfolioChatListItem onOpen={() => onOpenChat(chat)} key={chat.id} chat={chat} />
        ))}
      </ul>
      {hasMoreChats && (
        <footer className="p-space-md" ref={refLoader}>
          <Box padding={{ y: "md" }}>
            <Inline justify="center">
              <Spinner size="md" />
            </Inline>
          </Box>
        </footer>
      )}
    </Card>
  );
}
